import React from "react"
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs"
import { EVENTS, useTelemetryEmitter } from "../event-emitter"
import type { TelemetryValue } from "../event-emitter"
import { tabsBaseItemStyles } from "../tabs-base"
import { Tooltip } from "../tooltip"
import { cn, extractText } from "../utils"
import type { StrictOmit } from "../utils"
import { useTabsOrientation } from "./tabs-context"

// =============================================================================
// Props
// =============================================================================

export type TabsTabProps = {
  /**
   * The tab's label. Compose an `Icon` before the text or a `CountBadge` after
   * it as needed.
   */
  children?: React.ReactNode
  /** Layout classes, such as margin */
  className?: string
  /** The value that selects this tab and shows the `Tabs.Panel` with the same value */
  value: TabsPrimitive.Tab.Value
  /**
   * Whether the tab can't be selected. A disabled tab stays focusable, so set
   * `tooltip` to explain why.
   */
  disabled?: boolean
  /**
   * Shown on hover and keyboard focus, and announced as the tab's description,
   * such as why a tab is `disabled` or what a panel holds when the label alone
   * doesn't say. A disabled tab stays focusable, so keyboard and screen reader
   * users reach its tooltip too.
   */
  tooltip?: React.ReactNode
  /**
   * Data to emit alongside the `button_click` event when this tab is clicked.
   * Merges with any ancestor `Telemetry` context (local values win). Set to
   * `false` to opt this tab out of emitting.
   */
  telemetry?: TelemetryValue
} & StrictOmit<
  React.ComponentPropsWithoutRef<"button">,
  // Owned by the tab, or wired up by Base UI
  | "children"
  | "className"
  | "disabled"
  | "value"
  | "role"
  | "type"
  | "tabIndex"
  | "aria-selected"
  | "aria-controls"
  | "aria-disabled"
>

// =============================================================================
// Component
// =============================================================================

/**
 * A tab, rendered as a `<button role="tab">`. It's selected when its `value`
 * matches the ancestor `Tabs`' value.
 *
 * Must be a descendant of `Tabs.List`.
 *
 * To change the vertical padding of a horizontal tab, set
 * `--tabs-item-padding-y` (e.g. `[--tabs-item-padding-y:1.25rem]`) rather than
 * a `py-*` class, so the focus ring stays around the content.
 */
export const TabsTab = React.forwardRef<HTMLButtonElement, TabsTabProps>(
  function TabsTab(
    {
      children,
      className,
      value,
      disabled,
      tooltip,
      telemetry,
      onClick,
      ...props
    },
    ref
  ) {
    const orientation = useTabsOrientation()
    const emitTelemetry = useTelemetryEmitter(telemetry)

    const handleClick: TabsPrimitive.Tab.Props["onClick"] = event => {
      emitTelemetry(EVENTS.BUTTON_CLICK, { text: extractText(children), value })
      onClick?.(event)
    }

    const tab = (
      <TabsPrimitive.Tab
        {...props}
        ref={ref}
        value={value}
        disabled={disabled}
        className={cn(tabsBaseItemStyles({ orientation }), className)}
        data-slot="tabs-tab"
        onClick={handleClick}
      >
        {children}
      </TabsPrimitive.Tab>
    )

    // Always wrapped, so setting or clearing `tooltip` doesn't remount the tab
    // and drop its focus
    return (
      <Tooltip disabled={!tooltip}>
        <Tooltip.Trigger render={tab} />
        <Tooltip.Content
          position={orientation === "vertical" ? "right" : "top"}
        >
          {tooltip}
        </Tooltip.Content>
      </Tooltip>
    )
  }
)

TabsTab.displayName = "Tabs.Tab"
